import React, { useState, type JSX } from 'react';
import arrowDown from '@/assets/svg/static/arrowDown.svg';
import originalIcon from '@/assets/svg/static/original.svg';
import sizeSvg from '@/assets/svg/static/size.svg';
import { ProductOfferStrip, SizePills } from '@/components/ui';
import { useViewabilityTracker } from '@/hooks/useViewabilityTracker';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import type { InfluencerVideo } from '@/types/Product.types';
import { formatIndianPrice, toTitleCase } from '@/utils/formatting';
import InfluencerReelCard from '../InfluencerReelCard';
import ProductDetailBottomCta from '../ProductDetailBottomCta';
import styles from './ProductDetailInfo.module.css';
import type { ProductDetailInfoProps } from './ProductDetailInfo.types';

interface ReelCardWithTrackingProps {
  video: InfluencerVideo;
  idx: number;
  productId: number;
  curatorName: string;
  onClick: () => void;
}

const ReelCardWithTracking = ({
  video,
  idx,
  productId,
  curatorName,
  onClick,
}: ReelCardWithTrackingProps): JSX.Element => {
  const viewRef = useViewabilityTracker<HTMLDivElement>({
    onViewable: () => {
      trackEvent(EVENTS.PDP_V2_REEL_CARD_VIEWED, {
        [PROPERTIES.PRODUCT_ID]: productId,
        [PROPERTIES.VIDEO_ID]: video.id,
        [PROPERTIES.VIDEO_INDEX]: idx,
        [PROPERTIES.VIDEO_URL]: video.videoUrl,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        [PROPERTIES.CURATOR_NAME]: curatorName,
      });
    },
    threshold: 0.5,
    once: true,
  });

  return (
    <div ref={viewRef}>
      <InfluencerReelCard video={video} onClick={onClick} />
    </div>
  );
};

export const ProductDetailInfo = React.memo(
  ({
    productDetails,
    selectedSize,
    onSizeSelect,
    onViewSizeChart,
    description,
    influencerVideos,
    onReelClick,
    curatorName,
    paymentStatus,
    onShareClick,
    onBuyClick,
  }: ProductDetailInfoProps): JSX.Element => {
    const [activeAccordion, setActiveAccordion] = useState<{
      description: boolean;
    }>({
      description: false,
    });

    const toggleAccordion = (): void => {
      const nextState = !activeAccordion.description;
      trackEvent(EVENTS.PDP_V2_DESCRIPTION_ACCORDION_TOGGLE, {
        [PROPERTIES.PRODUCT_ID]: productDetails.id,
        [PROPERTIES.IS_OPEN]: nextState,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
      setActiveAccordion((prev) => ({
        ...prev,
        description: nextState,
      }));
    };

    const cleanProductName = (
      brand?: string,
      productName?: string
    ): string | undefined => {
      if (!brand || !productName) return productName;
      try {
        const regex = new RegExp(brand, 'gi');
        return productName.replace(regex, '').trim();
      } catch (err) {
        console.error(err);
        return productName;
      }
    };

    const mrpVal = parseFloat(productDetails.price || '0');
    const discountVal = parseFloat(productDetails.discountPrice || '0');
    let discountPercentage = 0;
    if (mrpVal > 0 && discountVal > 0 && discountVal < mrpVal) {
      discountPercentage = Math.round(((mrpVal - discountVal) / mrpVal) * 100);
    }

    return (
      <div className={styles.productInfo}>
        <div className={styles.brandNameRow}>
          <img
            src={originalIcon}
            className={styles.verifiedCheckIcon}
            alt="Verified Brand"
          />
          <span className={styles.brandName}>
            {toTitleCase(productDetails.brand)}
          </span>
        </div>

        <h2 className={styles.productName}>
          {toTitleCase(
            cleanProductName(
              productDetails.brand ?? '',
              productDetails.name ?? ''
            ) ?? ''
          )}
        </h2>

        <div className={styles.priceContainer}>
          <span className={styles.price}>
            ₹{formatIndianPrice(String(productDetails.discountPrice))}
          </span>
          {productDetails.price &&
            productDetails.price !== productDetails.discountPrice && (
              <>
                <span className={styles.mrp}>
                  ₹{formatIndianPrice(String(productDetails.price))}
                </span>
                {discountPercentage > 0 && (
                  <span className={styles.discountPercent}>
                    {discountPercentage}% off
                  </span>
                )}
              </>
            )}
        </div>

        {(productDetails.brandOffer || productDetails.pricingOffer) && (
          <ProductOfferStrip
            brandOffer={productDetails.brandOffer}
            pricingOffer={productDetails.pricingOffer}
          />
        )}

        <div className={styles.dividerLine} />

        {productDetails.availableSizes && (
          <div className={styles.sizeSection}>
            <div className={styles.sizeSectionHeader}>
              <span className={styles.sizeTitle}>SIZE</span>
            </div>

            <div className={styles.sizeContainer}>
              <div className={styles.sizePillsWrapper}>
                <SizePills
                  availableSizes={productDetails.availableSizes}
                  selectedSize={selectedSize}
                  onSizeSelect={onSizeSelect}
                />
              </div>

              {productDetails.sizeChartUrl && (
                <button
                  className={styles.viewSizeChartBtn}
                  onClick={() => {
                    trackEvent(EVENTS.PDP_V2_VIEW_SIZE_CHART_CLICK, {
                      [PROPERTIES.PRODUCT_ID]: productDetails.id,
                      [PROPERTIES.HAS_URL]: !!productDetails.sizeChartUrl,
                      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
                    });
                    onViewSizeChart();
                  }}
                >
                  <img
                    src={sizeSvg}
                    className={styles.sizeChartIcon}
                    alt="Ruler Icon"
                  />
                  <span>VIEW SIZE CHART</span>
                </button>
              )}
            </div>
            <div className={styles.dividerLine} />
          </div>
        )}

        <ProductDetailBottomCta
          isAvailable={productDetails.isAvailable ?? false}
          paymentStatus={paymentStatus}
          onShareClick={onShareClick}
          onBuyClick={onBuyClick}
        />
        <div className={styles.dividerLine} />

        {description && (
          <div>
            <div className={styles.accordionHeader} onClick={toggleAccordion}>
              <span>Product Detail</span>
              <img
                src={arrowDown}
                className={`${styles.accordionChevron} ${activeAccordion.description ? styles.chevronRotate : ''}`}
                alt="Chevron"
              />
            </div>
            {activeAccordion.description && (
              <div className={styles.accordionContent}>
                {description.replace(/<[^>]*>/g, '')}
              </div>
            )}
            <div className={styles.dividerLine} />
          </div>
        )}

        {influencerVideos.length > 0 && (
          <div
            id="influencer-picks-section"
            className={styles.influencerPicksSection}
          >
            <h3 className={styles.influencerTitle}>TOP INFLUENCER PICKS</h3>
            <div className={styles.influencerScroll}>
              {influencerVideos.map((video, idx) => (
                <ReelCardWithTracking
                  key={video.id}
                  video={video}
                  idx={idx}
                  productId={productDetails.id}
                  curatorName={curatorName}
                  onClick={() => onReelClick(idx)}
                />
              ))}
            </div>
            <div className={styles.dividerLine} />
          </div>
        )}
      </div>
    );
  }
);

export default ProductDetailInfo;
